import { Box, Paper, Stack, Typography, alpha, useTheme } from "@mui/material";
import AnimatedNumber from "@/components/AnimatedNumber";
import { formatInteger, formatPercent } from "../utils";

type MetricCardProps = {
  label: string;
  value: number;
  formatValue?: (value: number) => string;
  previousValue?: number | string;
  deltaPercent?: number;
  deltaLabel?: string;
  detail?: string;
  previousValueLabel?: string;
};

export default function MetricCard({
  label,
  formatValue = formatInteger,
  value,
  previousValue,
  deltaPercent,
  deltaLabel,
  detail,
  previousValueLabel,
}: MetricCardProps) {
  const theme = useTheme();
  const hasDelta = deltaLabel !== undefined || deltaPercent !== undefined;
  const isPositive = deltaLabel !== undefined || (deltaPercent ?? 0) >= 0;
  const deltaColor = isPositive ? "success.main" : "error.main";

  return (
    <Paper variant="outlined" sx={{ height: "100%", padding: 2 }}>
      <Stack gap={0.75} sx={{ height: "100%" }}>
        <Box
          sx={{
            alignItems: "flex-start",
            display: "flex",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <Typography color="text.secondary" variant="body2">
            {label}
          </Typography>

          {hasDelta &&
            (deltaLabel ? (
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  borderRadius: 1,
                  color: "success.main",
                  flexShrink: 0,
                  padding: "2px 6px",
                  textAlign: "right",
                }}
              >
                {deltaLabel}
              </Typography>
            ) : (
              <AnimatedNumber
                value={deltaPercent ?? 0}
                formatValue={formatPercent}
                variant="caption"
                sx={{
                  backgroundColor: alpha(
                    isPositive
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                    0.1
                  ),
                  borderRadius: 1,
                  color: deltaColor,
                  flexShrink: 0,
                  padding: "2px 6px",
                  textAlign: "right",
                }}
              />
            ))}
        </Box>

        <AnimatedNumber
          value={value}
          formatValue={formatValue}
          variant="h5"
        />

        {detail && (
          <Typography color="text.secondary" variant="caption">
            {detail}
          </Typography>
        )}

        {previousValue !== undefined && previousValueLabel && (
          <Typography
            color="text.secondary"
            sx={{ marginTop: "auto" }}
            variant="caption"
          >
            {previousValueLabel}:{" "}
            {typeof previousValue === "number"
              ? formatInteger(previousValue)
              : previousValue}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
